import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import LanguageToggle from '../LanguageToggle';
import { LayoutDashboard, MapPin, Star, Share2, Search, MessageSquare, Settings, LogOut, Menu, X, Building, Wifi, Mail, Sparkles, Phone, CreditCard, Target, FileText, AlertTriangle, ShieldCheck, Zap, Coins, Globe2, ClipboardCheck, MessageSquarePlus } from 'lucide-react';
import ClientOverview from './ClientOverview';
import ClientListings from './ClientListings';
import ClientReviews from './ClientReviews';
import ClientSocial from './ClientSocial';
import ClientSearch from './ClientSearch';
import ClientQA from './ClientQA';
import ClientBusinessProfile from './ClientBusinessProfile';
import ClientConnections from './ClientConnections';
import ClientSetupWizard from './ClientSetupWizard';
import ClientEmailMarketing from './ClientEmailMarketing';
import ClientSMS from './ClientSMS';
import ClientActivation from './ClientActivation';
import ClientBilling from './ClientBilling';
import ClientLocalSEO from './ClientLocalSEO';
import ClientReports from './ClientReports';
import ClientCallTracking from './ClientCallTracking';
import ClientSecuritySettings from './ClientSecuritySettings';
import ClientUpgrades from './ClientUpgrades';
import ClientBusinessLinks from './ClientBusinessLinks';
import GeoFencingUpsell from './GeoFencingUpsell';
import ClientCredits from './ClientCredits';
import ClientCitations from './ClientCitations';
import ClientGBPAudit from './ClientGBPAudit';
import ClientRankTracker from './ClientRankTracker';
import ClientFeedback from './ClientFeedback';
import FeedbackWidget from './FeedbackWidget';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', labelKey: 'overview', icon: LayoutDashboard },
  { id: 'call-tracking', labelKey: 'callTracking', icon: Phone },
  { id: 'profile', labelKey: 'profile', icon: Building },
  { id: 'connections', labelKey: 'connections', icon: Wifi },
  { id: 'listings', labelKey: 'listings', icon: MapPin },
  { id: 'reviews', labelKey: 'reviews', icon: Star },
  { id: 'social', labelKey: 'social', icon: Share2 },
  { id: 'email', labelKey: 'email', icon: Mail },
  { id: 'sms', labelKey: 'sms', icon: Phone },
  { id: 'search', labelKey: 'search', icon: Search },
  { id: 'citations', labelKey: 'citations', icon: Globe2 },
  { id: 'gbp-audit', labelKey: 'gbpAudit', icon: ClipboardCheck },
  { id: 'rank-tracker', labelKey: 'rankTracker', icon: Target },
  { id: 'questions', labelKey: 'questions', icon: MessageSquare },
  { id: 'credits', labelKey: 'credits', icon: Coins },
  { id: 'billing', labelKey: 'billing', icon: CreditCard },
  { id: 'local-seo', labelKey: 'localSeo', icon: Target },
  { id: 'reports', labelKey: 'reports', icon: FileText },
  { id: 'upgrades', labelKey: 'upgrades', icon: Zap },
  { id: 'advertising', labelKey: 'advertising', icon: Target },
  { id: 'business-links', labelKey: 'businessLinks', icon: Globe2 },
  { id: 'feedback', labelKey: 'feedback', icon: MessageSquarePlus },
  { id: 'security', labelKey: 'security', icon: ShieldCheck },
];

export default function ClientDashboard() {
  const { t } = useTranslation('dashboard');
  const [activeTab, setActiveTab] = useState('overview');
  const [client, setClient] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(true);
  const navigate = useNavigate();

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('client_token')}`,
  }), []);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) { navigate('/client/login'); return; }
    const stored = localStorage.getItem('client_data');
    if (stored) setClient(JSON.parse(stored));

    // Handle SMS recharge success redirect
    if (window.location.pathname.includes('sms-recharge-success')) {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const isPaypal = params.get('paypal');
      const paypalToken = params.get('token');

      if (sessionId) {
        // Stripe flow
        fetch(`${API}/api/client/sms/billing/recharge-verify?session_id=${sessionId}`)
          .then(r => r.json())
          .then(d => {
            if (d.success) {
              setActiveTab('sms');
              window.history.replaceState({}, '', '/client/dashboard');
            }
          })
          .catch(() => {});
      } else if (isPaypal && paypalToken) {
        // PayPal flow — capture the order
        const slug = params.get('slug');
        const tier = params.get('tier');
        // PayPal returns token param which is the order ID
        fetch(`${API}/api/client/sms/billing/recharge-paypal-capture`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order_id: paypalToken }),
        })
          .then(r => r.json())
          .then(d => {
            if (d.success) {
              setActiveTab('sms');
              window.history.replaceState({}, '', '/client/dashboard');
            }
          })
          .catch(() => {});
      }
    }

    // Handle auto-recharge card setup redirect
    if (window.location.pathname.includes('auto-recharge-setup')) {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const slug = params.get('slug');
      if (sessionId && slug) {
        fetch(`${API}/api/client/sms/billing/auto-recharge/verify-card`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId, slug }),
        })
          .then(r => r.json())
          .then(d => {
            if (d.success) {
              setActiveTab('sms');
              window.history.replaceState({}, '', '/client/dashboard');
            }
          })
          .catch(() => {});
      }
    }

    // Handle GBP OAuth callback redirect
    const gbpParam = new URLSearchParams(window.location.search).get('gbp');
    if (gbpParam === 'success') {
      setActiveTab('gbp-audit');
      window.history.replaceState({}, '', '/client/dashboard');
    } else if (gbpParam === 'error') {
      setActiveTab('gbp-audit');
      window.history.replaceState({}, '', '/client/dashboard');
    }

    // Handle Meta OAuth callback redirect
    const metaParam = new URLSearchParams(window.location.search).get('meta');
    if (metaParam === 'success') {
      setActiveTab('social');
      window.history.replaceState({}, '', '/client/dashboard');
    } else if (metaParam === 'error') {
      setActiveTab('social');
      window.history.replaceState({}, '', '/client/dashboard');
    }

    // Handle LinkedIn OAuth callback redirect (LinkedInConnect shows the notice & clears params)
    const liParams = new URLSearchParams(window.location.search);
    if (liParams.get('linkedin_connected') || liParams.get('linkedin_error')) {
      setActiveTab('connections');
    }

    fetch(`${API}/api/client/me`, { headers: getHeaders() })
      .then(r => {
        if (r.status === 401 || r.status === 403) { localStorage.clear(); navigate('/client/login'); return null; }
        return r.json();
      })
      .then(d => {
        if (d) {
          setClient(d);
          // Sync UI language to client's stored preferred_language
          if (d.preferred_language && d.preferred_language !== i18n.language) {
            i18n.changeLanguage(d.preferred_language);
          }
          // Check payment status
          if (d.slug) {
            fetch(`${API}/api/client/payments/status/${d.slug}`, { headers: getHeaders() })
              .then(r => r.json())
              .then(ps => setPaymentStatus(ps))
              .catch(() => setPaymentStatus({ setup_paid: false }))
              .finally(() => setCheckingPayment(false));
          } else {
            setCheckingPayment(false);
          }
        }
      })
      .catch(() => { setCheckingPayment(false); });
  }, [navigate, getHeaders]);

  // Persist UI language changes back to the server (debounced)
  useEffect(() => {
    const onLangChange = (lng) => {
      const token = localStorage.getItem('client_token');
      if (!token || !client?.slug) return;
      const lang = (lng === 'es' ? 'es' : 'en');
      if (client.preferred_language === lang) return;
      fetch(`${API}/api/client/dashboard/preferred-language`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferred_language: lang }),
      }).catch(() => {});
      setClient((c) => c ? { ...c, preferred_language: lang } : c);
    };
    i18n.on('languageChanged', onLangChange);
    return () => { i18n.off('languageChanged', onLangChange); };
  }, [client?.slug, client?.preferred_language, getHeaders]);

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    navigate('/client/login');
  };

  const branding = client?.branding || {};
  const primaryColor = branding.primary_color || '#2563eb';

  const renderTab = () => {
    if (showWizard) return <ClientSetupWizard client={client} getHeaders={getHeaders} onComplete={() => setShowWizard(false)} />;
    const props = { client, getHeaders };
    switch (activeTab) {
      case 'overview': return <ClientOverview {...props} />;
      case 'call-tracking': return <ClientCallTracking {...props} />;
      case 'profile': return <ClientBusinessProfile {...props} />;
      case 'connections': return <ClientConnections {...props} />;
      case 'listings': return <ClientListings {...props} />;
      case 'reviews': return <ClientReviews {...props} />;
      case 'social': return <ClientSocial {...props} />;
      case 'email': return <ClientEmailMarketing {...props} />;
      case 'sms': return <ClientSMS {...props} />;
      case 'search': return <ClientSearch {...props} />;
      case 'citations': return <ClientCitations {...props} />;
      case 'gbp-audit': return <ClientGBPAudit {...props} />;
      case 'rank-tracker': return <ClientRankTracker {...props} />;
      case 'questions': return <ClientQA {...props} />;
      case 'credits': return <ClientCredits {...props} />;
      case 'billing': return <ClientBilling {...props} />;
      case 'local-seo': return <ClientLocalSEO {...props} />;
      case 'reports': return <ClientReports {...props} />;
      case 'upgrades': return <ClientUpgrades {...props} />;
      case 'business-links': return <ClientBusinessLinks {...props} />;
      case 'advertising': return (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('advertising.title')}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{t('advertising.subtitle')}</p>
          </div>
          <GeoFencingUpsell {...props} variant="full" />
        </div>
      );
      case 'security': return <ClientSecuritySettings {...props} />;
      case 'feedback': return <ClientFeedback {...props} />;
      default: return <ClientOverview {...props} />;
    }
  };

  if (!client) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-pulse text-gray-400">{t('shell.loading')}</div>
    </div>
  );

  // Gate: Show activation/payment screen if setup fee not paid
  if (!checkingPayment && paymentStatus && !paymentStatus.setup_paid) {
    return (
      <ClientActivation
        slug={client.slug}
        token={localStorage.getItem('client_token')}
        onActivated={() => {
          setPaymentStatus(prev => ({ ...prev, setup_paid: true }));
        }}
      />
    );
  }

  // Gate: Suspended account — show billing tab directly
  if (!checkingPayment && paymentStatus && paymentStatus.account_status === 'suspended') {
    return (
      <ClientActivation
        slug={client.slug}
        token={localStorage.getItem('client_token')}
        onActivated={() => {
          setPaymentStatus(prev => ({ ...prev, account_status: 'active' }));
        }}
        suspended={true}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="client-dashboard">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static z-40 h-full w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-gray-200">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="" className="h-8 mb-2" />
          ) : (
            <div className="text-xl font-bold tracking-tight" style={{color: primaryColor}}>
              {branding.company_name || client?.business_name || 'Dashboard'}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-1 truncate">{client?.email}</p>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-auto">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  active
                    ? 'font-medium'
                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                }`}
                style={active ? {backgroundColor: `${primaryColor}15`, color: primaryColor} : {}}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {t(`tabs.${tab.labelKey}`)}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-200 space-y-1">
          <button
            onClick={() => { setShowWizard(true); setSidebarOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-all"
            data-testid="quick-setup-btn"
          >
            <Sparkles className="h-4 w-4" /> {t('shell.quickSetup')}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" /> {t('shell.signOut')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 py-3 flex items-center gap-4">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-gray-500 hover:text-gray-900">
            <Menu className="h-5 w-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900">
            {showWizard ? t('shell.quickSetup') : (TABS.find(tb => tb.id === activeTab) ? t(`tabs.${TABS.find(tb => tb.id === activeTab).labelKey}`) : t('tabs.dashboard'))}
          </h2>
          <span className="ml-auto text-xs text-gray-500 hidden sm:block">
            {t('shell.headerSubtitle', { businessName: client?.business_name, plan: (client?.package?.charAt(0).toUpperCase() + client?.package?.slice(1)) || '' })}
          </span>
          <div className="ml-auto sm:ml-3 flex-shrink-0">
            <LanguageToggle />
          </div>
        </header>

        <div className="p-4 lg:p-6 max-w-7xl mx-auto">
          {/* Payment Warning Banner */}
          {client?.payment_warning && (
            <div className={`mb-4 rounded-lg px-4 py-3 flex items-center gap-3 ${client.access_allowed === false ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`} data-testid="payment-warning-banner">
              <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${client.access_allowed === false ? 'text-red-500' : 'text-yellow-500'}`} />
              <p className={`text-sm ${client.access_allowed === false ? 'text-red-700' : 'text-yellow-700'}`}>
                {client.payment_warning}
              </p>
              <a href="/client/billing" className="ml-auto text-xs font-medium px-3 py-1.5 rounded-md bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 whitespace-nowrap" data-testid="update-payment-btn">
                {t('shell.paymentWarningCta')}
              </a>
            </div>
          )}
          {client?.access_allowed === false ? (
            <div className="text-center py-20" data-testid="access-restricted-screen">
              <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('shell.accountRestrictedTitle')}</h2>
              <p className="text-gray-500 max-w-md mx-auto mb-6">{t('shell.accountRestrictedDesc')}</p>
              <a href="/client/billing" className="inline-block px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold transition-colors" data-testid="restore-access-btn">
                {t('shell.restoreAccessBtn')}
              </a>
            </div>
          ) : renderTab()}
        </div>
      </main>
      {client?.access_allowed !== false && (
        <FeedbackWidget getHeaders={getHeaders} primaryColor={primaryColor} />
      )}
    </div>
  );
}
